import AccountSelect from "../components/AccountSelect";
import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { api, useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { CurrencyInput } from "../components/ui/currency-input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Progress } from "../components/ui/progress";
import { toast } from "@/lib/toast";
import { formatMoney } from "@/lib/currency";
import { Plus, TrendingDown, Trash2, Loader2, CreditCard, History, DollarSign, Calendar, Percent, Edit } from "lucide-react";

const DEBT_COLORS = ["#0e7490", "#f97316", "#10b981", "#3b82f6", "#8b5cf6"];

const DebtsPage = () => {
  const { user } = useAuth();
  const { isExpired } = useOutletContext() || {};
  const userCurrency = user?.currency || "COP";
  
  const [debts, setDebts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [editPaymentDialogOpen, setEditPaymentDialogOpen] = useState(false);
  const [selectedDebt, setSelectedDebt] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [paymentAccount, setPaymentAccount] = useState("unassigned");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentPrincipal, setPaymentPrincipal] = useState("");
  const [paymentInterest, setPaymentInterest] = useState("");
  const [paymentNote, setPaymentNote] = useState("");
  const [autoCalculate, setAutoCalculate] = useState(true);
  
  const [formData, setFormData] = useState({
    name: "",
    total_amount: "",
    remaining_amount: "",
    monthly_payment: "",
    interest_rate: "",
    interest_type: "annual",
    payment_day: "",
    total_installments: "",
    pending_installments: "",
    principal_per_payment: "",
    interest_per_payment: "",
    currency: userCurrency
  });

  useEffect(() => {
    fetchDebts();
  }, []);

  const fetchDebts = async () => {
    try {
      const res = await api.get("/debts");
      setDebts(res.data);
    } catch (error) {
      toast.error("Error al cargar deudas");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.total_amount || !formData.remaining_amount) {
      toast.error("Completa los campos requeridos");
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/debts", {
        ...formData,
        total_amount: parseFloat(formData.total_amount),
        remaining_amount: parseFloat(formData.remaining_amount),
        monthly_payment: formData.monthly_payment ? parseFloat(formData.monthly_payment) : null,
        interest_rate: formData.interest_rate ? parseFloat(formData.interest_rate) : null,
        interest_type: formData.interest_type || "annual",
        payment_day: formData.payment_day ? parseInt(formData.payment_day) : null,
        total_installments: formData.total_installments ? parseInt(formData.total_installments) : null,
        pending_installments: formData.pending_installments ? parseInt(formData.pending_installments) : null,
        principal_per_payment: formData.principal_per_payment ? parseFloat(formData.principal_per_payment) : null,
        interest_per_payment: formData.interest_per_payment ? parseFloat(formData.interest_per_payment) : null
      });
      toast.success("Deuda registrada");
      setDialogOpen(false);
      setFormData({ 
        name: "", total_amount: "", remaining_amount: "", monthly_payment: "", interest_rate: "",
        interest_type: "annual", payment_day: "", total_installments: "", pending_installments: "", 
        principal_per_payment: "", interest_per_payment: "", currency: userCurrency 
      });
      fetchDebts();
    } catch (error) {
      toast.error("Error al crear deuda");
    } finally {
      setSubmitting(false);
    }
  };

  const openEditDialog = (debt) => {
    setSelectedDebt(debt);
    setFormData({
      name: debt.name || "",
      total_amount: debt.total_amount?.toString() || "",
      remaining_amount: debt.remaining_amount?.toString() || "",
      monthly_payment: debt.monthly_payment?.toString() || "",
      interest_rate: debt.interest_rate?.toString() || "",
      interest_type: debt.interest_type || "annual",
      payment_day: debt.payment_day?.toString() || "",
      total_installments: debt.total_installments?.toString() || "",
      pending_installments: debt.pending_installments?.toString() || "",
      principal_per_payment: debt.principal_per_payment?.toString() || "",
      interest_per_payment: debt.interest_per_payment?.toString() || "",
      currency: debt.currency || userCurrency
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDebt || !formData.name || !formData.total_amount || !formData.remaining_amount) {
      toast.error("Completa los campos requeridos");
      return;
    }

    setSubmitting(true);
    try {
      await api.put(`/debts/${selectedDebt.id}`, {
        name: formData.name,
        total_amount: parseFloat(formData.total_amount),
        remaining_amount: parseFloat(formData.remaining_amount),
        monthly_payment: formData.monthly_payment ? parseFloat(formData.monthly_payment) : null,
        interest_rate: formData.interest_rate ? parseFloat(formData.interest_rate) : null,
        interest_type: formData.interest_type || "annual",
        payment_day: formData.payment_day ? parseInt(formData.payment_day) : null,
        total_installments: formData.total_installments ? parseInt(formData.total_installments) : null,
        pending_installments: formData.pending_installments ? parseInt(formData.pending_installments) : null,
        principal_per_payment: formData.principal_per_payment ? parseFloat(formData.principal_per_payment) : null,
        interest_per_payment: formData.interest_per_payment ? parseFloat(formData.interest_per_payment) : null,
        currency: formData.currency
      });
      toast.success("Deuda actualizada");
      setEditDialogOpen(false);
      setSelectedDebt(null);
      setFormData({ 
        name: "", total_amount: "", remaining_amount: "", monthly_payment: "", interest_rate: "",
        interest_type: "annual", payment_day: "", total_installments: "", pending_installments: "", 
        principal_per_payment: "", interest_per_payment: "", currency: userCurrency 
      });
      fetchDebts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar deuda");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/debts/${id}`);
      toast.success("Deuda eliminada");
      fetchDebts();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const openEditPaymentDialog = (payment) => {
    setSelectedPayment(payment);
    setPaymentAmount(payment.amount?.toString() || "");
    setPaymentPrincipal(payment.principal_amount?.toString() || "");
    setPaymentInterest(payment.interest_amount?.toString() || "");
    setPaymentNote(payment.note || "");
    setAutoCalculate(false);
    setEditPaymentDialogOpen(true);
  };

  const handleEditPayment = async () => {
    if (!selectedDebt || !selectedPayment || !paymentAmount) return;
    
    setSubmitting(true);
    try {
      await api.put(`/debts/${selectedDebt.id}/payments/${selectedPayment.id}`, {
        amount: parseFloat(paymentAmount),
        principal_amount: paymentPrincipal ? parseFloat(paymentPrincipal) : null,
        interest_amount: paymentInterest ? parseFloat(paymentInterest) : null,
        note: paymentNote || null
      });
      toast.success("Pago actualizado");
      setEditPaymentDialogOpen(false);
      resetPaymentForm();
      fetchDebts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar pago");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!selectedDebt) return;
    
    if (!window.confirm("¿Eliminar este pago? El monto se sumará nuevamente al saldo de la deuda.")) return;
    
    try {
      await api.delete(`/debts/${selectedDebt.id}/payments/${paymentId}`);
      toast.success("Pago eliminado");
      fetchDebts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar pago");
    }
  };

  const resetPaymentForm = () => {
    setPaymentAmount("");
    setPaymentPrincipal("");
    setPaymentInterest("");
    setPaymentNote("");
    setAutoCalculate(true);
    setSelectedPayment(null);
  };

  const handleAddPayment = async () => {
    if (!selectedDebt || !paymentAmount) return;
    
    setSubmitting(true);
    try {
      const paymentData = {
        account_id: paymentAccount,
        amount: parseFloat(paymentAmount),
        note: paymentNote || null
      };
      
      // If manual mode, include principal and interest
      if (!autoCalculate && paymentPrincipal) {
        paymentData.principal_amount = parseFloat(paymentPrincipal);
        paymentData.interest_amount = paymentInterest ? parseFloat(paymentInterest) : null;
      }
      
      const res = await api.post(`/debts/${selectedDebt.id}/payments`, paymentData);
      
      const msg = res.data.principal_paid && res.data.interest_paid 
        ? `Abono registrado: ${formatCurrency(res.data.principal_paid)} a capital, ${formatCurrency(res.data.interest_paid)} a intereses`
        : "Abono registrado exitosamente";
      
      toast.success(msg);
      setPaymentDialogOpen(false);
      setPaymentAmount("");
      setPaymentPrincipal("");
      setPaymentInterest("");
      setPaymentNote("");
      setAutoCalculate(true);
      setSelectedDebt(null);
      fetchDebts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al registrar abono");
    } finally {
      setSubmitting(false);
    }
  };

  const openPaymentDialog = (debt) => {
    setSelectedDebt(debt);
    setPaymentDialogOpen(true);
  };

  const openHistoryDialog = async (debt) => {
    try {
      const res = await api.get(`/debts/${debt.id}/payments`);
      setSelectedDebt({ ...debt, ...res.data });
      setHistoryDialogOpen(true);
    } catch (error) {
      toast.error("Error al cargar historial");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return formatMoney(amount, userCurrency);
  };

  const totalDebt = debts.reduce((sum, d) => sum + d.remaining_amount, 0);

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-32 bg-slate-200 dark:bg-slate-800 rounded-2xl"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1,2].map(i => (
            <div key={i} className="h-40 bg-slate-200 dark:bg-slate-800 rounded-2xl"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="debts-page">
      {/* Total Debt Card */}
      <Card className="bg-gradient-to-r from-cyan-900 to-cyan-700 text-white border-none shadow-xl">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-cyan-100 text-sm font-medium">Deuda Total Pendiente</p>
              <h2 className="text-3xl sm:text-4xl font-bold mt-2 font-['Outfit']" data-testid="total-debt">
                {formatCurrency(totalDebt)}
              </h2>
              {debts.length > 0 && (
                <p className="text-cyan-200 text-xs mt-4 flex items-center gap-1">
                  <TrendingDown className="w-4 h-4" /> 
                  {debts.length} {debts.length === 1 ? 'deuda activa' : 'deudas activas'}
                </p>
              )}
            </div>
            <Dialog open={dialogOpen} onOpenChange={(open) => (!open || !isExpired) && setDialogOpen(open)}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-white/20 hover:bg-white/30 text-white border-none shrink-0" 
                  size="sm"
                  data-testid="add-debt-btn"
                  disabled={isExpired}
                >
                  <Plus className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Nueva Deuda</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-['Outfit']">Nueva Deuda</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nombre de la deuda *</Label>
                    <Input
                      id="name"
                      placeholder="Ej: Préstamo Auto"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      data-testid="debt-name-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="total">Monto Total *</Label>
                      <CurrencyInput
                        id="total"
                        currency={userCurrency}
                        placeholder="0"
                        value={formData.total_amount}
                        onChange={(e) => setFormData({...formData, total_amount: e.target.value})}
                        data-testid="debt-total-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="remaining">Monto Restante *</Label>
                      <CurrencyInput
                        id="remaining"
                        currency={userCurrency}
                        placeholder="0"
                        value={formData.remaining_amount}
                        onChange={(e) => setFormData({...formData, remaining_amount: e.target.value})}
                        data-testid="debt-remaining-input"
                      />
                    </div>
                  </div>

                  {/* Información de cuotas */}
                  <div className="border-t pt-4 mt-4">
                    <p className="text-sm text-slate-500 mb-3">Información de cuotas (opcional)</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="monthly">Cuota Mensual</Label>
                        <CurrencyInput
                          id="monthly"
                          currency={userCurrency}
                          placeholder="0"
                          value={formData.monthly_payment}
                          onChange={(e) => setFormData({...formData, monthly_payment: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="payment_day">Día de Pago</Label>
                        <Input
                          id="payment_day"
                          type="number"
                          min="1"
                          max="31"
                          placeholder="15"
                          value={formData.payment_day}
                          onChange={(e) => setFormData({...formData, payment_day: e.target.value})}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="total_inst">Cuotas Totales</Label>
                      <Input
                        id="total_inst"
                        type="number"
                        min="1"
                        placeholder="36"
                        value={formData.total_installments}
                        onChange={(e) => setFormData({...formData, total_installments: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="pending_inst">Cuotas Pendientes</Label>
                      <Input
                        id="pending_inst"
                        type="number"
                        min="0"
                        placeholder="24"
                        value={formData.pending_installments}
                        onChange={(e) => setFormData({...formData, pending_installments: e.target.value})}
                      />
                    </div>
                  </div>

                  {/* Información de intereses */}
                  <div className="border-t pt-4 mt-4">
                    <p className="text-sm text-slate-500 mb-3">Distribución de cuota (opcional)</p>
                    
                    {/* Interest rate with type selector */}
                    <div className="flex gap-2 mb-3">
                      <div className="flex-1 space-y-2">
                        <Label htmlFor="interest">Tasa de Interés %</Label>
                        <Input
                          id="interest"
                          type="number"
                          step="0.1"
                          placeholder="18.0"
                          value={formData.interest_rate}
                          onChange={(e) => setFormData({...formData, interest_rate: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Tipo</Label>
                        <div className="flex rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
                          <button
                            type="button"
                            className={`px-3 py-2 text-xs font-medium transition-colors ${
                              formData.interest_type === 'annual' 
                                ? 'bg-cyan-600 text-white' 
                                : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200'
                            }`}
                            onClick={() => setFormData({...formData, interest_type: 'annual'})}
                          >
                            Anual
                          </button>
                          <button
                            type="button"
                            className={`px-3 py-2 text-xs font-medium transition-colors ${
                              formData.interest_type === 'monthly' 
                                ? 'bg-cyan-600 text-white' 
                                : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200'
                            }`}
                            onClick={() => setFormData({...formData, interest_type: 'monthly'})}
                          >
                            Mensual
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="principal_pp">A Capital</Label>
                        <CurrencyInput
                          id="principal_pp"
                          currency={userCurrency}
                          placeholder="0"
                          value={formData.principal_per_payment}
                          onChange={(e) => setFormData({...formData, principal_per_payment: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="interest_pp">A Intereses</Label>
                        <CurrencyInput
                          id="interest_pp"
                          currency={userCurrency}
                          placeholder="0"
                          value={formData.interest_per_payment}
                          onChange={(e) => setFormData({...formData, interest_per_payment: e.target.value})}
                        />
                      </div>
                    </div>
                    <p className="text-xs text-slate-400 mt-2">
                      💡 Si tienes el % de interés, el sistema calcula automáticamente la distribución al registrar pagos
                    </p>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full btn-primary"
                    disabled={submitting}
                    data-testid="submit-debt-btn"
                  >
                    {submitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      "Guardar Deuda"
                    )}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </CardContent>
      </Card>

      {/* Debts Grid */}
      {debts.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {debts.map((debt, i) => {
            const paidPercent = ((debt.total_amount - debt.remaining_amount) / debt.total_amount) * 100;
            const color = DEBT_COLORS[i % DEBT_COLORS.length];
            
            return (
              <Card key={debt.id} className="stat-card group" data-testid={`debt-${debt.id}`}>
                <CardContent className="p-6">
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: `${color}20` }}
                      >
                        <CreditCard className="w-5 h-5" style={{ color }} />
                      </div>
                      <h4 className="font-bold">{debt.name}</h4>
                    </div>
                    {!isExpired && (
                      <button
                        onClick={() => handleDelete(debt.id)}
                        className="p-2 opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-500 transition-all"
                        data-testid={`delete-debt-${debt.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  <div className="flex justify-between items-center mb-3">
                    <span className="text-sm text-slate-500">Restante</span>
                    <span className="font-bold">
                      {formatCurrency(debt.remaining_amount)} / {formatCurrency(debt.total_amount)}
                    </span>
                  </div>

                  <div className="debt-progress mb-4">
                    <div 
                      className="h-full rounded-full transition-all duration-1000"
                      style={{ width: `${100 - paidPercent}%`, backgroundColor: color }}
                    />
                  </div>

                  <div className="flex justify-between text-xs text-slate-500 mb-2">
                    <span>Pagado: {paidPercent.toFixed(0)}%</span>
                    {debt.monthly_payment && (
                      <span className="text-orange-500 font-bold">
                        Cuota: {formatCurrency(debt.monthly_payment)}
                      </span>
                    )}
                  </div>

                  {/* Additional debt info */}
                  <div className="grid grid-cols-2 gap-2 text-xs text-slate-500 mt-2">
                    {debt.pending_installments && (
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        <span>{debt.pending_installments}/{debt.total_installments || '?'} cuotas</span>
                      </div>
                    )}
                    {debt.payment_day && (
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3 text-cyan-500" />
                        <span>Pago día {debt.payment_day}</span>
                      </div>
                    )}
                    {debt.interest_rate && (
                      <div className="flex items-center gap-1">
                        <Percent className="w-3 h-3 text-amber-500" />
                        <span>{debt.interest_rate}% {debt.interest_type === 'monthly' ? 'mensual' : 'anual'}</span>
                      </div>
                    )}
                    {debt.principal_per_payment && debt.interest_per_payment && (
                      <div className="text-xs">
                        <span className="text-emerald-600">${debt.principal_per_payment.toLocaleString()}</span>
                        <span className="text-slate-400"> + </span>
                        <span className="text-amber-600">${debt.interest_per_payment.toLocaleString()}</span>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100 dark:border-slate-700">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 text-xs"
                      onClick={() => openPaymentDialog(debt)}
                      disabled={isExpired}
                    >
                      <DollarSign className="w-3 h-3 mr-1" />
                      Abonar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 text-xs"
                      onClick={() => openEditDialog(debt)}
                      disabled={isExpired}
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Editar
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-xs"
                      onClick={() => openHistoryDialog(debt)}
                    >
                      <History className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="stat-card">
          <CardContent className="p-12 text-center">
            <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 mb-4">No tienes deudas registradas</p>
            <Button onClick={() => !isExpired && setDialogOpen(true)} className="btn-accent" disabled={isExpired}>
              <Plus className="w-4 h-4 mr-2" />
              Agregar primera deuda
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-500" />
              Registrar Abono
            </DialogTitle>
          </DialogHeader>
          {selectedDebt && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                <p className="text-sm font-medium">{selectedDebt.name}</p>
                <p className="text-xs text-slate-500">
                  Saldo pendiente: {formatCurrency(selectedDebt.remaining_amount)}
                </p>
                {selectedDebt.pending_installments && (
                  <p className="text-xs text-orange-500 mt-1">
                    Cuotas pendientes: {selectedDebt.pending_installments} de {selectedDebt.total_installments || '?'}
                  </p>
                )}
              </div>
              
              <div className="space-y-2">
                <AccountSelect value={paymentAccount} onChange={setPaymentAccount} />
                <Label htmlFor="payment-amount">Monto total del pago</Label>
                <CurrencyInput
                  id="payment-amount"
                  currency={userCurrency}
                  placeholder="0"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                />
              </div>

              {/* Toggle for manual distribution */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="auto-calc"
                  checked={autoCalculate}
                  onChange={(e) => setAutoCalculate(e.target.checked)}
                  className="rounded border-slate-300"
                />
                <Label htmlFor="auto-calc" className="text-sm cursor-pointer">
                  Calcular distribución automáticamente
                </Label>
              </div>

              {!autoCalculate && (
                <div className="grid grid-cols-2 gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                  <div className="space-y-1">
                    <Label htmlFor="payment-principal" className="text-xs">A Capital</Label>
                    <CurrencyInput
                      id="payment-principal"
                      currency={userCurrency}
                      placeholder="0"
                      value={paymentPrincipal}
                      onChange={(e) => setPaymentPrincipal(e.target.value)}
                      className="h-8"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="payment-interest" className="text-xs">A Intereses</Label>
                    <CurrencyInput
                      id="payment-interest"
                      currency={userCurrency}
                      placeholder="0"
                      value={paymentInterest}
                      onChange={(e) => setPaymentInterest(e.target.value)}
                      className="h-8"
                    />
                  </div>
                </div>
              )}

              {autoCalculate && selectedDebt.interest_rate && (
                <p className="text-xs text-slate-500 bg-slate-100 dark:bg-slate-800 p-2 rounded">
                  💡 Se calculará usando la tasa de {selectedDebt.interest_rate}% anual
                </p>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="payment-note">Nota (opcional)</Label>
                <Input
                  id="payment-note"
                  placeholder="Ej: Pago mensual enero"
                  value={paymentNote}
                  onChange={(e) => setPaymentNote(e.target.value)}
                />
              </div>
              
              <Button 
                className="w-full btn-primary"
                onClick={handleAddPayment}
                disabled={submitting || !paymentAmount}
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Registrar Abono"
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-cyan-500" />
              Historial de Pagos
            </DialogTitle>
          </DialogHeader>
          {selectedDebt && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                <p className="font-bold">{selectedDebt.debt_name || selectedDebt.name}</p>
                <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                  <div>
                    <p className="text-slate-500">Total</p>
                    <p className="font-medium">{formatCurrency(selectedDebt.total_amount)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Pendiente</p>
                    <p className="font-medium text-orange-500">{formatCurrency(selectedDebt.remaining_amount)}</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-600">Pagos realizados:</p>
                {selectedDebt.payments && selectedDebt.payments.length > 0 ? (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {selectedDebt.payments.map((payment, idx) => (
                      <div 
                        key={payment.id || idx} 
                        className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg"
                      >
                        <div className="flex justify-between items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-emerald-700 dark:text-emerald-400">
                              {formatCurrency(payment.amount)}
                            </p>
                            <p className="text-xs text-slate-500 flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(payment.date)}
                            </p>
                            {(payment.principal_amount || payment.interest_amount) && (
                              <div className="text-xs mt-1">
                                <span className="text-emerald-600">Cap: {formatCurrency(payment.principal_amount || 0)}</span>
                                <span className="text-slate-400 mx-1">|</span>
                                <span className="text-amber-600">Int: {formatCurrency(payment.interest_amount || 0)}</span>
                              </div>
                            )}
                            {payment.note && (
                              <p className="text-xs text-slate-400 mt-1">{payment.note}</p>
                            )}
                          </div>
                          {!isExpired && (
                            <div className="flex gap-1 shrink-0">
                              <button
                                onClick={() => openEditPaymentDialog(payment)}
                                className="p-1.5 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded"
                                title="Editar"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeletePayment(payment.id)}
                                className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded"
                                title="Eliminar"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-4">
                    No hay pagos registrados aún
                  </p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Payment Dialog */}
      <Dialog open={editPaymentDialogOpen} onOpenChange={(open) => {
        setEditPaymentDialogOpen(open);
        if (!open) resetPaymentForm();
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-cyan-500" />
              Editar Pago
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-payment-amount">Monto total del pago</Label>
              <CurrencyInput
                id="edit-payment-amount"
                currency={userCurrency}
                placeholder="0"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="edit-payment-principal" className="text-xs">A Capital</Label>
                <CurrencyInput
                  id="edit-payment-principal"
                  currency={userCurrency}
                  placeholder="0"
                  value={paymentPrincipal}
                  onChange={(e) => setPaymentPrincipal(e.target.value)}
                  className="h-8"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="edit-payment-interest" className="text-xs">A Intereses</Label>
                <CurrencyInput
                  id="edit-payment-interest"
                  currency={userCurrency}
                  placeholder="0"
                  value={paymentInterest}
                  onChange={(e) => setPaymentInterest(e.target.value)}
                  className="h-8"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-payment-note">Nota (opcional)</Label>
              <Input
                id="edit-payment-note"
                placeholder="Ej: Pago mensual enero"
                value={paymentNote}
                onChange={(e) => setPaymentNote(e.target.value)}
              />
            </div>

            <Button 
              className="w-full btn-primary"
              onClick={handleEditPayment}
              disabled={submitting || !paymentAmount}
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Guardar Cambios"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Debt Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(open) => {
        setEditDialogOpen(open);
        if (!open) {
          setSelectedDebt(null);
          setFormData({ 
            name: "", total_amount: "", remaining_amount: "", monthly_payment: "", interest_rate: "",
            interest_type: "annual", payment_day: "", total_installments: "", pending_installments: "", 
            principal_per_payment: "", interest_per_payment: "", currency: userCurrency 
          });
        }
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-cyan-500" />
              Editar Deuda
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Nombre de la deuda *</Label>
              <Input
                id="edit-name"
                placeholder="Ej: Préstamo Auto"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-total">Monto Total *</Label>
                <Input
                  id="edit-total"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.total_amount}
                  onChange={(e) => setFormData({...formData, total_amount: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-remaining">Monto Restante *</Label>
                <Input
                  id="edit-remaining"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.remaining_amount}
                  onChange={(e) => setFormData({...formData, remaining_amount: e.target.value})}
                />
              </div>
            </div>

            {/* Información de cuotas */}
            <div className="border-t pt-4 mt-4">
              <p className="text-sm text-slate-500 mb-3">Información de cuotas</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-monthly">Cuota Mensual</Label>
                  <Input
                    id="edit-monthly"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={formData.monthly_payment}
                    onChange={(e) => setFormData({...formData, monthly_payment: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-payment_day">Día de Pago</Label>
                  <Input
                    id="edit-payment_day"
                    type="number"
                    min="1"
                    max="31"
                    placeholder="15"
                    value={formData.payment_day}
                    onChange={(e) => setFormData({...formData, payment_day: e.target.value})}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-total_inst">Cuotas Totales</Label>
                <Input
                  id="edit-total_inst"
                  type="number"
                  min="1"
                  placeholder="36"
                  value={formData.total_installments}
                  onChange={(e) => setFormData({...formData, total_installments: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-pending_inst">Cuotas Pendientes</Label>
                <Input
                  id="edit-pending_inst"
                  type="number"
                  min="0"
                  placeholder="24"
                  value={formData.pending_installments}
                  onChange={(e) => setFormData({...formData, pending_installments: e.target.value})}
                />
              </div>
            </div>

            {/* Información de intereses */}
            <div className="border-t pt-4 mt-4">
              <p className="text-sm text-slate-500 mb-3">Distribución de cuota</p>
              
              {/* Interest rate with type selector */}
              <div className="flex gap-2 mb-3">
                <div className="flex-1 space-y-2">
                  <Label htmlFor="edit-interest">Tasa de Interés %</Label>
                  <Input
                    id="edit-interest"
                    type="number"
                    step="0.1"
                    placeholder="18.0"
                    value={formData.interest_rate}
                    onChange={(e) => setFormData({...formData, interest_rate: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tipo</Label>
                  <div className="flex rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
                    <button
                      type="button"
                      className={`px-3 py-2 text-xs font-medium transition-colors ${
                        formData.interest_type === 'annual' 
                          ? 'bg-cyan-600 text-white' 
                          : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200'
                      }`}
                      onClick={() => setFormData({...formData, interest_type: 'annual'})}
                    >
                      Anual
                    </button>
                    <button
                      type="button"
                      className={`px-3 py-2 text-xs font-medium transition-colors ${
                        formData.interest_type === 'monthly' 
                          ? 'bg-cyan-600 text-white' 
                          : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200'
                      }`}
                      onClick={() => setFormData({...formData, interest_type: 'monthly'})}
                    >
                      Mensual
                    </button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="edit-principal_pp">A Capital</Label>
                  <Input
                    id="edit-principal_pp"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={formData.principal_per_payment}
                    onChange={(e) => setFormData({...formData, principal_per_payment: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-interest_pp">A Intereses</Label>
                  <Input
                    id="edit-interest_pp"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={formData.interest_per_payment}
                    onChange={(e) => setFormData({...formData, interest_per_payment: e.target.value})}
                  />
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-2">
                💡 Si tienes el % de interés, el sistema calcula automáticamente la distribución al registrar pagos
              </p>
            </div>

            <Button 
              type="submit" 
              className="w-full btn-primary"
              disabled={submitting}
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Guardar Cambios"
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DebtsPage;
